/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  createDailyMission,
  createRewardPromotion,
  createUserLevel,
  getAllDailyMissions,
  getAllRewardPromotions,
  getAllUserLevels,
  getDailyMissionById,
  getRewardPromotionById,
  getUserLevelById,
  updateDailyMission,
  updateRewardPromotion,
  updateUserLevel,
} from '../actions/rewardsAdmin';
import { RewardsAdminState } from '../types/rewardsAdmin';

const initialState: RewardsAdminState = {
  userLevels: [],
  isFetchingUserLevels: false,
  editingUserLevel: null,
  missions: [],
  isFetchingMissions: false,
  editingMission: null,
  promotions: [],
  isFetchingPromotions: false,
  editingPromotion: null,
  isSaving: false,
};

const rewardsAdminSlice = createSlice({
  name: 'rewardsAdmin',
  initialState,
  reducers: {
    // Cleared when the form unmounts, so opening Create straight after Edit
    // does not inherit the previous row's values.
    clearEditingUserLevel: (state) => {
      state.editingUserLevel = null;
    },
    clearEditingMission: (state) => {
      state.editingMission = null;
    },
    clearEditingPromotion: (state) => {
      state.editingPromotion = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllUserLevels.pending, (state) => {
        state.isFetchingUserLevels = true;
      })
      .addCase(getAllUserLevels.fulfilled, (state, action) => {
        state.isFetchingUserLevels = false;
        state.userLevels = action.payload.user_levels ?? [];
      })
      .addCase(getAllUserLevels.rejected, (state) => {
        state.isFetchingUserLevels = false;
      })
      .addCase(getUserLevelById.fulfilled, (state, action) => {
        state.editingUserLevel = action.payload;
      })
      .addCase(getAllDailyMissions.pending, (state) => {
        state.isFetchingMissions = true;
      })
      .addCase(getAllDailyMissions.fulfilled, (state, action) => {
        state.isFetchingMissions = false;
        state.missions = action.payload.daily_missions ?? [];
      })
      .addCase(getAllDailyMissions.rejected, (state) => {
        state.isFetchingMissions = false;
      })
      .addCase(getDailyMissionById.fulfilled, (state, action) => {
        state.editingMission = action.payload;
      })
      .addCase(getAllRewardPromotions.pending, (state) => {
        state.isFetchingPromotions = true;
      })
      .addCase(getAllRewardPromotions.fulfilled, (state, action) => {
        state.isFetchingPromotions = false;
        state.promotions = action.payload.reward_promotions ?? [];
      })
      .addCase(getAllRewardPromotions.rejected, (state) => {
        state.isFetchingPromotions = false;
      })
      .addCase(getRewardPromotionById.fulfilled, (state, action) => {
        state.editingPromotion = action.payload;
      })
      // One `isSaving` across every entity and verb — only one form is ever
      // mounted, so a flag per (entity × verb) would be six booleans that can
      // never disagree.
      .addMatcher(
        (action) =>
          [
            createUserLevel.pending.type,
            updateUserLevel.pending.type,
            createDailyMission.pending.type,
            updateDailyMission.pending.type,
            createRewardPromotion.pending.type,
            updateRewardPromotion.pending.type,
          ].includes(action.type),
        (state) => {
          state.isSaving = true;
        },
      )
      .addMatcher(
        (action) =>
          [
            createUserLevel.fulfilled.type,
            createUserLevel.rejected.type,
            updateUserLevel.fulfilled.type,
            updateUserLevel.rejected.type,
            createDailyMission.fulfilled.type,
            createDailyMission.rejected.type,
            updateDailyMission.fulfilled.type,
            updateDailyMission.rejected.type,
            createRewardPromotion.fulfilled.type,
            createRewardPromotion.rejected.type,
            updateRewardPromotion.fulfilled.type,
            updateRewardPromotion.rejected.type,
          ].includes(action.type),
        (state) => {
          state.isSaving = false;
        },
      );
  },
});

export const {
  clearEditingUserLevel,
  clearEditingMission,
  clearEditingPromotion,
} = rewardsAdminSlice.actions;

export default rewardsAdminSlice.reducer;
